import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

export function ExportButton() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data, headers } = await api.get("/inventory-export", { responseType: "blob" });
      const name = (headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "catalogue.xlsx";
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
      toast.success("Catalogue exporté");
    } catch { toast.error("Export impossible"); }
    finally { setBusy(false); }
  };
  return (
    <Button data-testid="stock-export-btn" variant="outline" onClick={run} disabled={busy} className="rounded-full border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800">
      {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}Exporter
    </Button>
  );
}

const FIELDS = [
  { k: "name", l: "Nom de l'article", req: true, guess: ["nom", "name", "article", "désignation", "designation", "produit", "libellé", "libelle"] },
  { k: "quantity", l: "Quantité", guess: ["quantité", "quantite", "qté", "qte", "qty", "quantity", "stock"] },
  { k: "category", l: "Catégorie", guess: ["catégorie", "categorie", "category", "famille"] },
  { k: "type", l: "Type (matériel / prestation / forfait / location)", guess: ["type"] },
  { k: "price", l: "Prix de location", guess: ["prix", "price", "tarif", "prix location", "tarif location", "prix ht"] },
  { k: "purchase_price", l: "Prix d'achat", guess: ["prix d'achat", "achat", "purchase", "coût", "cout"] },
  { k: "deposit_amount", l: "Caution", guess: ["caution", "dépôt", "depot", "deposit"] },
  { k: "description", l: "Description", guess: ["description", "desc", "détail", "detail"] },
];

const norm = (s) => (s || "").toString().trim().toLowerCase();
const guessMap = (cols) => {
  const m = Object.fromEntries(FIELDS.map((f) => [f.k, ""]));
  const used = new Set();
  const pass = (match) => FIELDS.forEach((f) => {
    if (m[f.k] !== "") return;
    const idx = cols.findIndex((c, i) => !used.has(i) && match(f, norm(c)));
    if (idx >= 0) { m[f.k] = String(idx); used.add(idx); }
  });
  pass((f, c) => f.guess.includes(c));
  pass((f, c) => f.guess.some((g) => c.includes(g)));
  return m;
};

const TEMPLATE = "Nom;Quantité;Catégorie;Type;Prix;Prix d'achat;Caution;Description\nEnceinte 15\";4;Son;matériel;45;350;200;Enceinte active 1000W\n";

export default function ImportDialog({ warehouses, onDone }) {
  const [open, setOpen] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [map, setMap] = useState({});
  const [loc, setLoc] = useState(warehouses[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setParsed(null); setMap({}); setResult(null); setBusy(false); };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setBusy(true);
    try { const { data } = await api.post("/inventory/import/parse", fd); setParsed({ ...data, filename: f.name }); setMap(guessMap(data.columns)); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Lecture du fichier impossible"); }
    finally { setBusy(false); }
  };
  const mapped = (parsed?.rows || []).map((r) => Object.fromEntries(FIELDS.map((f) => [f.k, map[f.k] !== "" && map[f.k] !== undefined ? r[Number(map[f.k])] : null])));
  const commit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/inventory/import/commit", { rows: mapped, location_id: loc }); setResult(data); onDone?.(); toast.success(`${data.created} créé(s), ${data.updated} mis à jour`); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Import impossible"); }
    finally { setBusy(false); }
  };
  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob(["\ufeff" + TEMPLATE], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = "modele-import-catalogue.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="stock-import-btn" variant="outline" className="rounded-full border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800"><Upload className="mr-1.5 h-4 w-4" />Importer</Button>
      </DialogTrigger>
      <DialogContent data-testid="stock-import-dialog" className="max-h-[90vh] max-w-3xl overflow-y-auto border-zinc-800 bg-zinc-950 text-white">
        <DialogHeader><DialogTitle>Importer un catalogue / stock</DialogTitle></DialogHeader>
        {result ? <ImportResult result={result} onAgain={reset} onClose={() => setOpen(false)} /> : !parsed ? (
          <div className="space-y-4">
            <label data-testid="stock-import-drop" className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-zinc-800 bg-zinc-900/40 px-6 py-12 text-center transition-colors hover:border-[#FF6A00]/50">
              {busy ? <Loader2 className="h-8 w-8 animate-spin text-[#FF6A00]" /> : <FileSpreadsheet className="h-10 w-10 text-[#FF6A00]" />}
              <span className="font-medium">Choisir un fichier CSV ou Excel (.xlsx)</span>
              <span className="text-xs text-zinc-500">1re ligne = en-têtes de colonnes · 5 Mo max</span>
              <input data-testid="stock-import-file" type="file" accept=".csv,.txt,.xlsx" className="hidden" onChange={onFile} disabled={busy} />
            </label>
            <button data-testid="stock-import-template" onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-sm text-[#FF6A00] hover:underline"><Download className="h-4 w-4" />Télécharger un modèle CSV</button>
          </div>
        ) : (
          <ImportMapping parsed={parsed} map={map} setMap={setMap} mapped={mapped} warehouses={warehouses} loc={loc} setLoc={setLoc}
            busy={busy} onBack={reset} onCommit={commit} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ImportMapping({ parsed, map, setMap, mapped, warehouses, loc, setLoc, busy, onBack, onCommit }) {
  const valid = mapped.filter((r) => (r.name || "").trim()).length;
  const shown = FIELDS.filter((f) => map[f.k] !== "");
  const sel = "w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-200";
  return (
    <div className="space-y-5">
      <p className="text-sm text-zinc-400"><span className="text-white">{parsed.filename}</span> · {parsed.rows.length} ligne(s){parsed.truncated ? " (limité à 5000)" : ""}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {FIELDS.map((f) => (
          <label key={f.k} className="space-y-1 text-xs text-zinc-500">
            <span>{f.l}{f.req && <span className="text-[#FF6A00]"> *</span>}</span>
            <select data-testid={`import-map-${f.k}`} value={map[f.k] ?? ""} onChange={(e) => setMap({ ...map, [f.k]: e.target.value })} className={sel}>
              <option value="">— Ne pas importer —</option>
              {parsed.columns.map((c, i) => <option key={i} value={String(i)}>{c}</option>)}
            </select>
          </label>
        ))}
        {warehouses.length > 0 && (
          <label className="space-y-1 text-xs text-zinc-500 sm:col-span-2">
            <span>Entrepôt où ranger les quantités</span>
            <select data-testid="import-warehouse" value={loc} onChange={(e) => setLoc(e.target.value)} className={sel}>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </label>
        )}
      </div>
      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">Aperçu (5 premières lignes)</p>
        <div className="overflow-x-auto rounded-xl border border-zinc-800">
          <table data-testid="import-preview" className="w-full text-left text-xs">
            <thead className="bg-zinc-900 text-zinc-500"><tr>{shown.map((f) => <th key={f.k} className="px-3 py-2 font-medium">{f.l}</th>)}</tr></thead>
            <tbody>{mapped.slice(0, 5).map((r, i) => (
              <tr key={i} className="border-t border-zinc-800">{shown.map((f) => <td key={f.k} className={`max-w-[180px] truncate px-3 py-2 ${f.k === "name" && !(r.name || "").trim() ? "text-red-400" : "text-zinc-300"}`}>{r[f.k] || (f.k === "name" ? "manquant" : "")}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-zinc-500">Un article portant déjà le même nom sera mis à jour ; les autres seront créés. Les colonnes « Ne pas importer » ne modifient rien.</p>
      </div>
      <div className="flex justify-between gap-2">
        <Button variant="ghost" onClick={onBack} className="text-zinc-400">Changer de fichier</Button>
        <Button data-testid="import-commit" disabled={busy || map.name === "" || !valid} onClick={onCommit} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">
          {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Importer {valid} article(s)
        </Button>
      </div>
    </div>
  );
}

function ImportResult({ result, onAgain, onClose }) {
  return (
    <div data-testid="import-result" className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4"><p className="text-2xl font-bold text-[#FF6A00]" data-testid="import-created">{result.created}</p><p className="text-xs text-zinc-500">article(s) créé(s)</p></div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4"><p className="text-2xl font-bold text-white" data-testid="import-updated">{result.updated}</p><p className="text-xs text-zinc-500">article(s) mis à jour</p></div>
      </div>
      {result.new_categories?.length > 0 && <p className="text-sm text-zinc-400">Nouvelles catégories ajoutées : {result.new_categories.join(", ")}</p>}
      {result.errors?.length > 0 && (
        <div data-testid="import-errors" className="rounded-xl border border-red-500/20 bg-red-500/5 p-3 text-sm text-red-300">
          {result.errors.length} ligne(s) ignorée(s) : {result.errors.slice(0, 10).map((e) => `ligne ${e.line} (${e.error})`).join(", ")}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onAgain} className="text-zinc-400">Importer un autre fichier</Button>
        <Button data-testid="import-close" onClick={onClose} className="rounded-full bg-[#FF6A00] text-white hover:bg-[#E65F00]">Terminer</Button>
      </div>
    </div>
  );
}
